import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Button, Tabs } from "antd";
import { ReactComponent as Cam } from "images/vendor/Cam.svg";
import { ReactComponent as LocationSvg } from "images/vendor/Location.svg";
import AccountDetails from "./accountDetails";
import Packages from "./packages";
import PastEvents from "./pastEvent";
import Ratings from "./ratings";
import { useGetOrganizerByEmail } from "lib/hooks";
import { useGetVendorById } from "lib/hooks";
import { useParams } from "react-router-dom";
import { ReactComponent as Completed } from "images/admin/completedicon.svg";
import { ReactComponent as Total } from "images/admin/totalamount.svg";

const Section = styled.div`
  margin-top: 1rem;
  margin-left: 10%;
  margin-right: 10%;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    margin-top: 3rem;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    width: 90%;
  }
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const TopLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const CamContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  margin-bottom: -90px;
  margin-left: 85px;
  cursor: pointer;
`;

const LeftParagraph = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BusinessName = styled.div`
  margin-top: 0;
  margin-bottom: 0;
`;

const LargText = styled.p`
  text-align: left;
  font-size: 40px;
  font-weight: 600;
  line-height: 48px;
  text-align: left;
  margin-top: 0;
  margin-bottom: 0;
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 0;
  }
`;

const Circle = styled.div<{ imageUrl?: string }>`
  width: 20vw;
  height: 20vw;
  max-width: 128px;
  max-height: 128px;
  border-radius: 50%;
  background-color: ${({ imageUrl }) => (imageUrl ? "transparent" : "#1390e6")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  position: relative;
  background-image: ${({ imageUrl }) => (imageUrl ? `url(${imageUrl})` : "none")};
  background-size: cover;
  background-position: center;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    width: 40vw;
    height: 40vw;
    font-size: 0.9rem;
  }
`;

const Location = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 400;
  font-size: 0.8rem;
  line-height: 1.5rem;
  gap: 10px;
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StyledButton = styled(Button)`
  width: 177px;
  height: 46px;
  border-radius: 8px;
  border: 1px solid rgba(226, 232, 240, 1);
  color: rgba(47, 47, 47, 1);
  font-weight: 500;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 20px;
  color: rgba(89, 89, 89, 1);
  width: 100%;
  padding: 10px;
`;

const ScrollableTabContainer = styled.div`
  display: flex;
  overflow-x: auto;
  width: 100%;
  padding: 10px;

  /* Hide scrollbar */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  margin-left: 10px;
  width: 100%;
`;

const TopItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const TopItemTop = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const TopItemBottom = styled.div`
  display: flex;
  flex-direction: row;
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const SmallTextAsh = styled.p`
  text-align: left;
  font-size: 14px;
  margin-top: 0;
  margin-bottom: 0;
  color: #595959;
`;

const SmallTextBlack = styled.p`
  text-align: left;
  font-size: 14px;
  margin-top: 0;
  margin-bottom: 0;
`;

const VerticalDivider = styled.div`
  height: 50px;
  width: 1px;
  background-color: #ede9e9;
  margin: 0 10px;
`;

const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const AdminVendorProfile: React.FC = () => {
  const [name, setName] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [initials, setInitials] = useState<string>("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const params = useParams<{ id?: string }>();
  const [organizerId, setOrganizerId] = useState<number>(0);

  useEffect(() => {
    const id = params.id ? parseInt(params.id) : undefined;
    if (id) setOrganizerId(id);
  }, [params.id]);

  const { data: vendorData, isLoading, error } = useGetVendorById({ id: organizerId });

  useEffect(() => {
    if (vendorData) {
      const fullName = `${capitalizeFirstLetter(vendorData.user.firstName)} ${capitalizeFirstLetter(vendorData.user.lastName)}`;
      setName(fullName);
      setInitials(
        `${vendorData.user.firstName.charAt(0).toUpperCase()}${vendorData.user.lastName.charAt(0).toUpperCase()}`,
      );

      const city = vendorData.city || "";
      const state = vendorData.state || "";

      if (city && state) {
        setLocation(`${city}, ${state}.`);
      } else {
        setLocation(" _ _ ");
      }

      if (vendorData.avatar) {
        setAvatarUrl(vendorData.avatar);
      }
    }
  }, [vendorData]);

  const items = [
    {
      key: "1",
      label: "Account Details",
      children: <AccountDetails vendor={vendorData} />,
    },
    {
      key: "2",
      label: "Packages",
      children: <Packages vendor={vendorData} />,
    },
    {
      key: "3",
      label: "Past Events",
      children: <PastEvents vendor={vendorData} />,
    },
    {
      key: "4",
      label: "Ratings and Reviews",
      children: <Ratings vendor={vendorData} />,
    },
  ];

  return (
    <Section>
      <TopContent>
        <TopLeft>
          <Circle imageUrl={avatarUrl || undefined}>{!avatarUrl && initials}</Circle>
          <LeftParagraph>
            <BusinessName>
              <LargText>{name}</LargText>
            </BusinessName>
          </LeftParagraph>
        </TopLeft>
      </TopContent>
      <Top>
        <TopItem>
          <TopItemTop>
            <Completed /> <SmallTextAsh>Completed requests</SmallTextAsh>
          </TopItemTop>
          <TopItemBottom>
            <SmallTextBlack>8 requests</SmallTextBlack>
          </TopItemBottom>
        </TopItem>
        <ItemContainer>
          <VerticalDivider />
          <TopItem>
            <TopItemTop>
              <Total />
              <SmallTextAsh>Total amount</SmallTextAsh>
            </TopItemTop>
            <TopItemBottom>
              <SmallTextBlack>NGN 30,000,000</SmallTextBlack>
            </TopItemBottom>
          </TopItem>
        </ItemContainer>
        <ItemContainer>
          <VerticalDivider />
          <TopItem>
            <TopItemTop>
              <Total />
              <SmallTextAsh>Joining Date</SmallTextAsh>
            </TopItemTop>
            <TopItemBottom>
              <SmallTextBlack>26 Dec 2023 at 04:00 PM</SmallTextBlack>
            </TopItemBottom>
          </TopItem>
        </ItemContainer>
      </Top>
      <ScrollableTabContainer>
        <Tabs defaultActiveKey="1" items={items} style={{ width: "100%" }} />
      </ScrollableTabContainer>
    </Section>
  );
};

export default AdminVendorProfile;
